import React from 'react';
import { Target, MapPin, Clock, DollarSign, Smartphone, Shield, Star, Award, Users, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { whyChooseUs, testimonials, companyStats, teamMembers } from '../data/mock';

const iconMap = {
  Target,
  MapPin,
  Clock,
  DollarSign,
  Smartphone,
  Shield
};

const WhyTriptoPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#6C30ED]/10 via-white to-[#FE4B79]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Why Choose
            <span className="block bg-gradient-to-r from-[#6C30ED] to-[#FE4B79] bg-clip-text text-transparent">
              TRIPTO?
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
            We don't just plan trips—we craft extraordinary journeys that exceed expectations. 
            Discover what makes TRIPTO the preferred choice for discerning travelers worldwide.
          </p>
          
          <div className="inline-flex items-center bg-white/70 backdrop-blur-sm rounded-full px-6 py-3 border border-gray-200 shadow-lg">
            <Star className="text-yellow-400 mr-2 fill-current" size={20} />
            <span className="font-semibold text-gray-900">4.9/5 Rating from 5,000+ Travelers</span>
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Sets Us Apart
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Six key pillars that form the foundation of our exceptional service and ensure 
              your travel experiences exceed every expectation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card 
                  key={index}
                  className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 bg-white text-center"
                >
                  <CardHeader className="pb-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#6C30ED]/10 to-[#4E8EFF]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent size={32} className="text-[#6C30ED]" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-[#6C30ED] transition-colors">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {item.description}
                    </p>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {item.details}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Statistics */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Proven Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our numbers tell the story of our commitment to exceptional travel experiences and customer satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {companyStats.map((stat, index) => (
              <Card key={index} className="text-center p-8 shadow-lg border-0 bg-white">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] bg-clip-text text-transparent mb-4">
                  {stat.number}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{stat.label}</h3>
                <p className="text-gray-600 leading-relaxed">{stat.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  TRIPTO was born from a simple belief: travel should be transformative, not transactional. 
                  Founded by passionate travelers who experienced the frustration of cookie-cutter tours and 
                  impersonal service, we set out to revolutionize how people explore the world.
                </p>
                <p>
                  Our journey began with a commitment to authentic experiences and genuine connections. 
                  We believe that the best travel memories come from unexpected moments, local encounters, 
                  and experiences that can't be found in guidebooks.
                </p>
                <p>
                  Today, TRIPTO stands as a testament to what's possible when passion meets expertise. 
                  We've helped thousands of travelers discover not just new places, but new perspectives on life itself.
                </p>
              </div>
              
              <div className="mt-8 flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#6C30ED]">2012</div>
                  <p className="text-sm text-gray-600">Founded</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#00DF98]">8</div>
                  <p className="text-sm text-gray-600">Countries</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#6C30ED]/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our passionate travel experts bring decades of combined experience and deep destination knowledge 
              to craft your perfect journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="text-center p-8 shadow-lg border-0 bg-white">
                <Avatar className="w-24 h-24 mx-auto mb-6">
                  <AvatarImage src={member.image} alt={member.name} />
                  <AvatarFallback>{member.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                </Avatar>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-[#6C30ED] font-semibold mb-4">{member.position}</p>
                <p className="text-gray-600 leading-relaxed mb-6">{member.bio}</p>
                <div className="space-y-2">
                  {member.expertise.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-block bg-gradient-to-r from-[#6C30ED]/10 to-[#4E8EFF]/10 text-[#6C30ED] text-sm px-3 py-1 rounded-full mr-2 mb-2"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* Testimonials Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Our Travelers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real stories from real travelers who chose TRIPTO for their extraordinary journeys.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 6).map((testimonial) => (
              <Card key={testimonial.id} className="p-6 shadow-lg border-0 bg-gradient-to-br from-white to-gray-50/50">
                <CardContent className="pt-0">
                  {/* Rating stars */}
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={16} 
                        className="text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  
                  <blockquote className="text-gray-700 leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </blockquote>
                  
                  <div className="flex items-center">
                    <Avatar className="w-12 h-12 mr-4">
                      <AvatarImage src={testimonial.image} alt={testimonial.name} />
                      <AvatarFallback>{testimonial.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.location}</div>
                      <div className="text-xs text-[#6C30ED] font-medium">{testimonial.trip}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-20 bg-gradient-to-r from-[#6C30ED]/5 via-white to-[#4E8EFF]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Awards & Recognition
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry recognition for our commitment to excellence and innovation in travel experiences.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { award: "Best DMC Asia", year: "2023", org: "Travel Weekly" },
              { award: "Excellence in Service", year: "2023", org: "TripAdvisor" },
              { award: "Top Travel Company", year: "2022", org: "Conde Nast" },
              { award: "Sustainable Tourism", year: "2022", org: "UNWTO" }
            ].map((award, index) => (
              <Card key={index} className="text-center p-6 shadow-lg border-0 bg-white">
                <Award size={32} className="text-[#00DF98] mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">{award.award}</h3>
                <p className="text-sm text-gray-600">{award.org}</p>
                <p className="text-xs text-[#6C30ED] font-medium">{award.year}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Experience the TRIPTO Difference?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied travelers who chose TRIPTO for their extraordinary journeys. 
            Let us show you what makes us different.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Start Your Journey
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#6C30ED] text-[#6C30ED] hover:bg-[#6C30ED] hover:text-white font-semibold px-8 py-4 rounded-full"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyTriptoPage;